import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as installationActions from '../../actions/installation';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import { toast } from 'react-toastify';
import '../Contact/Contact.scss';
import '../Transport/Transport.scss';

const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

interface Props {
    waitingItems: any[];
    loading: boolean;
    actions: any;
    history: any;
    match: any;
}

const InstallationForm: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const currentUsername = localStorage.getItem('username') || '';

    const [waitingItem, setWaitingItem] = useState<any>(null);
    const [comments, setComments] = useState<string[]>([]);
    const [newComment, setNewComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [loadingItem, setLoadingItem] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(props.history.location.search);
        const tiId = params.get('ti');
        const wrId = params.get('wr');

        if (tiId) {
            setLoadingItem(true);
            const found = (props.waitingItems || []).find((item: any) => String(item.transportItemId || item.id) === tiId);
            if (found) {
                setWaitingItem(found);
                setLoadingItem(false);
            } else {
                props.actions.fetchWaitingItem(parseInt(tiId)).then((data: any) => {
                    if (data) setWaitingItem(data);
                    setLoadingItem(false);
                });
            }
        } else if (wrId) {
            setLoadingItem(true);
            const found = (props.waitingItems || []).find((item: any) => item.source === 'warehouse' && String(item.warehouseRecordId) === wrId);
            if (found) {
                setWaitingItem(found);
                setLoadingItem(false);
            } else {
                props.actions.fetchWaitingWr(parseInt(wrId)).then((data: any) => {
                    if (data) setWaitingItem(data);
                    setLoadingItem(false);
                });
            }
        }
    }, []);

    const addComment = () => {
        if (newComment.trim()) {
            setComments(prev => [...prev, newComment.trim()]);
            setNewComment('');
        }
    };

    const handleSubmit = async () => {
        if (!waitingItem) { toast.error(fm('installation.form.toast.noItem', 'Nuk ka artikull të zgjedhur')); return; }

        setSaving(true);
        try {
            const payload: any = {
                comments: comments.length > 0 ? comments : undefined,
            };

            if (waitingItem.source === 'warehouse') {
                payload.warehouse_record_id = waitingItem.warehouseRecordId;
            } else {
                payload.transport_item_id = waitingItem.transportItemId || waitingItem.id;
            }

            const result = await props.actions.createInstallation(payload);
            toast.success(fm('installation.form.toast.created', 'Instalimi u krijua'));
            if (result?.data?.id) props.history.push(`/dashboard/installation/${result.data.id}`);
            else props.history.push('/dashboard/installation');
        } catch (err) {
            toast.error(fm('installation.form.toast.somethingWrong', 'Diçka shkoi gabim'));
        }
        setSaving(false);
    };

    const parseCoords = (loc: string | null) => {
        if (!loc) return null;
        const parts = loc.split(',').map(s => parseFloat(s.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return parts;
        return null;
    };

    const item = waitingItem;
    const coords = item ? parseCoords(item.measureLocation) : null;
    const googleMapsUrl = coords ? `https://www.google.com/maps/dir/?api=1&destination=${coords[0]},${coords[1]}` : null;

    return (
        <div className="clp-page" style={{ gap: 12 }}>
            <MikaPageHeader
                title={fm('installation.form.createTitle', 'Krijo Instalim të Ri')}
                subtitle={item ? `${item.client?.name || '-'} — ${item.project?.name || '-'}` : fm('installation.form.loadingSubtitle', 'Duke u ngarkuar...')}
                actions={
                    <button onClick={handleSubmit} disabled={saving || !waitingItem}
                        style={{
                            padding: '7px 18px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)',
                            background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 500,
                            cursor: saving || !waitingItem ? 'wait' : 'pointer', opacity: saving || !waitingItem ? 0.5 : 1,
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget).style.background = 'rgba(255,255,255,0.22)'; }}
                        onMouseLeave={(e) => { (e.currentTarget).style.background = 'rgba(255,255,255,0.12)'; }}>
                        {saving ? fm('installation.form.saving', 'Duke ruajtur...') : fm('installation.form.createAction', 'Krijo instalimin')}
                    </button>
                }
            />

            {loadingItem && <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>{fm('installation.form.loadingSubtitle', 'Duke u ngarkuar...')}</div>}

            {!loadingItem && !item && (
                <div style={{ background: '#f8fafc', border: '2px dashed #d0dbe8', borderRadius: 8, padding: '40px 20px', textAlign: 'center', color: '#94a3b8' }}>
                    <div style={{ fontSize: 13 }}>{fm('installation.form.itemNotFound', 'Nuk u gjet artikulli. Kthehu tek lista e pritjes.')}</div>
                </div>
            )}

            {item && (
                <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Delivery card */}
                    <div>
                        <div className="t-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                            <FormattedMessage id="installation.form.delivery" defaultMessage="Dorëzimi" />
                            <span className="t-card-header-badge">{fm('installation.card.productsCount', '{count} produkte', { count: item.products?.length || 0 })}</span>
                        </div>
                        <div className="t-card-body">
                            <div className="t-delivery-info">
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></div>
                                    <div className="t-delivery-value">{item.client?.firstName || item.client?.name || '-'} {item.client?.lastName || ''}</div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="installation.form.phone" defaultMessage="Telefoni" /></div>
                                    <div className="t-delivery-value">{item.client?.phone || <span className="t-delivery-value-empty">—</span>}</div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="installation.form.location" defaultMessage="Lokacioni" /></div>
                                    <div className="t-delivery-value">
                                        {googleMapsUrl ? (
                                            <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="t-maps-link">
                                                <svg width="11" height="14" viewBox="3 1 18 21" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>{fm('installation.form.googleMaps', 'Google Maps')}
                                            </a>
                                        ) : <span className="t-delivery-value-empty"><FormattedMessage id="installation.form.noLocation" defaultMessage="Pa lokacion" /></span>}
                                    </div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="installation.col.city" defaultMessage="Qyteti" /></div>
                                    <div className="t-delivery-value">{item.client?.city || <span className="t-delivery-value-empty">—</span>}</div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="installation.col.country" defaultMessage="Shteti" /></div>
                                    <div className="t-delivery-value">{item.client?.country || <span className="t-delivery-value-empty">—</span>}</div>
                                </div>
                                <div className="t-delivery-field">
                                    <div className="t-delivery-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></div>
                                    <div className="t-delivery-value">{item.project?.name || <span className="t-delivery-value-empty">—</span>}</div>
                                </div>
                            </div>
                            <div className="t-products-section">
                                <div className="t-wr-header">
                                    <span className="t-wr-label">{item.warehouseLabel || '-'}</span>
                                    <span className="t-wr-badge">{fm('installation.card.productsCount', '{count} produkte', { count: item.products?.length || 0 })}</span>
                                </div>
                                {(item.products || []).length > 0 ? item.products.map((p: any, pIdx: number) => (
                                    <div key={pIdx} className="t-product-row">
                                        <span className="t-product-name">{p.productName || fm('installation.form.unnamed', 'Pa emër')}</span>
                                        {p.type && <span className="t-product-type">{p.type}</span>}
                                        <span className="t-product-qty">{p.quantity ? `${p.quantity} ${p.unit || ''}`.trim() : '-'}</span>
                                    </div>
                                )) : <div className="t-product-empty"><FormattedMessage id="installation.form.noProducts" defaultMessage="Pa produkte" /></div>}
                            </div>
                        </div>
                    </div>

                    {/* Comments */}
                    <div>
                        <div className="t-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            <FormattedMessage id="installation.form.comments" defaultMessage="Komentet" />
                        </div>
                        <div className="t-card-body">
                            {comments.map((c, idx) => (
                                <div key={idx} style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 6, borderLeft: '3px solid #2a5a8a' }}>
                                    <div style={{ fontSize: 13, color: '#0f172a' }}>{c}</div>
                                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{currentUsername} — {fm('installation.form.now', 'tani')}</div>
                                </div>
                            ))}
                            <div style={{ display: 'flex', gap: 8 }}>
                                <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                                    placeholder={fm('installation.form.commentPlaceholder', 'Shkruaj një koment...')}
                                    style={{ ...inputStyle, flex: 1, minHeight: 40, resize: 'vertical' }} />
                                <button onClick={addComment} style={{
                                    padding: '8px 14px', borderRadius: 6, border: 'none', alignSelf: 'flex-end',
                                    background: '#1e3a5f', color: '#fff', fontSize: 12, cursor: 'pointer',
                                }}>{fm('installation.form.addComment', 'Shto')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    waitingItems: state.installation.waitingItems,
    loading: state.app.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(installationActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InstallationForm) as any) as any;
